import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import CustomerDropdown from './CustomerDropdown';
import { ApiError, getDepositDetail, saveDepositAllocations } from './customerCenterApi';
import { toMoneyValue } from './money';
import type { BranchId, Customer, CustomerAllocation, DecimalAmount, DepositDetail, DepositId } from './types';

export interface DepositCustomerCellProps {
  branchId: BranchId;
  depositId: DepositId;
  description: string;
  /** Opens the split editor for a deposit already split across customers. */
  onEditSplits: (depositId: DepositId) => void;
}

type CellState =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'ready'; allocations: CustomerAllocation[]; depositAmount: DecimalAmount | null; suggestedCustomer: Customer | null };

function toCustomer(allocation: CustomerAllocation): Customer {
  return {
    id: allocation.customer_id,
    name: allocation.customer_name ?? String(allocation.customer_id),
    active: allocation.active ?? false,
  };
}

function readyState(detail: DepositDetail): CellState {
  return {
    status: 'ready',
    allocations: detail.allocations,
    depositAmount: detail.deposit_amount,
    suggestedCustomer: detail.suggested_customer
      ? { id: detail.suggested_customer.id, name: detail.suggested_customer.name, active: true }
      : null,
  };
}

/**
 * Customer for one bank deposit on Deposit Classification. Shows the saved customer,
 * saves a pick as one full-amount assignment, and never overwrites a multi-customer
 * split: that case links to the split editor instead.
 */
export default function DepositCustomerCell({
  branchId,
  depositId,
  description,
  onEditSplits,
}: DepositCustomerCellProps) {
  const { t } = useTranslation('scenes.glclassification.deposit');
  const [state, setState] = useState<CellState>({ status: 'loading' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading' });
    getDepositDetail(branchId, depositId, controller.signal).then(
      (detail) => setState(readyState(detail)),
      () => {
        if (!controller.signal.aborted) setState({ status: 'failed' });
      },
    );
    return () => controller.abort();
  }, [branchId, depositId]);

  if (state.status === 'failed') return <span className="text-danger">{t('customer.load_failed')}</span>;

  const allocations = state.status === 'ready' ? state.allocations : [];
  if (allocations.length > 1) {
    return (
      <Button variant="link" className="p-0" onClick={() => onEditSplits(depositId)}>
        {t('customer.split', { count: allocations.length })}
      </Button>
    );
  }

  // A pick assigns the whole bank deposit (all Deposit legs), not the clicked row's leg.
  const amount = state.status === 'ready' ? toMoneyValue(state.depositAmount) : null;
  const handlePick = (customer: Customer | null) => {
    if (amount === null) return;
    setSaving(true);
    const assignments = customer ? [{ customer_id: Number(customer.id), amount }] : [];
    saveDepositAllocations(branchId, depositId, assignments).then(
      (detail) => {
        setState(readyState(detail));
        setSaving(false);
        toast.success(customer ? t('customer.saved') : t('customer.cleared'));
      },
      (err: unknown) => {
        setSaving(false);
        const messageText = err instanceof ApiError ? err.messageText : null;
        const message = err instanceof Error ? err.message : '';
        toast.error(messageText || message || t('customer.save_failed'));
      },
    );
  };

  const suggestedCustomer = state.status === 'ready' ? state.suggestedCustomer : null;

  return (
    <CustomerDropdown
      // Default options load once at mount; remount so the suggestion that arrives with the detail is listed.
      key={suggestedCustomer ? `suggested-${suggestedCustomer.id}` : 'no-suggestion'}
      branchId={branchId}
      disabled={state.status === 'loading' || saving || amount === null || Number(amount) === 0}
      customer={allocations.length === 1 ? toCustomer(allocations[0]) : null}
      suggestedCustomer={allocations.length === 0 ? suggestedCustomer : null}
      ariaLabel={t('customer.dropdown_for', { description })}
      placeholder={t('customer.placeholder')}
      onPick={handlePick}
    />
  );
}
